import React from 'react';
import './BusinessCard.css';

const formatTime = (hour, minute) => {
  const h = hour || 0;
  const m = minute || 0;
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const getTodayHoursInfo = (hours) => {
  if (!hours || hours.length === 0) return null;
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayIdx = new Date().getDay();
  const today = days[todayIdx];
  const todayHours = hours.filter(h => h.day === today);

  const findNextOpen = (startOffset = 1) => {
    for (let i = 0; i < 7; i++) {
      const checkIdx = (todayIdx + startOffset + i) % 7;
      const checkDay = days[checkIdx];
      const checkHours = hours.filter(h => h.day === checkDay && !h.isClosed);
      if (checkHours.length > 0) {
        const first = checkHours[0];
        return {
          dayLabel: dayNames[checkIdx],
          timeLabel: formatTime(first.fromHour, first.fromMinute),
          isTomorrow: i === 0 && startOffset === 1,
        };
      }
    }
    return null;
  };

  if (todayHours.length === 0) {
    const next = findNextOpen(1);
    return next
      ? { status: 'closed', text: 'Closed today', nextOpen: next.timeLabel, nextOpenDay: next.dayLabel }
      : { status: 'closed', text: 'Closed today', nextOpen: null, nextOpenDay: null };
  }

  if (todayHours.some(h => h.isClosed)) {
    const next = findNextOpen(1);
    return next
      ? { status: 'closed', text: 'Closed today', nextOpen: next.timeLabel, nextOpenDay: next.dayLabel }
      : { status: 'closed', text: 'Closed today', nextOpen: null, nextOpenDay: null };
  }

  const ranges = todayHours.map(h => {
    const fromMin = (h.fromHour || 0) * 60 + (h.fromMinute || 0);
    const toMin = (h.toHour || 0) * 60 + (h.toMinute || 0);
    const fromLabel = formatTime(h.fromHour, h.fromMinute);
    const toLabel = formatTime(h.toHour, h.toMinute);
    return { fromMin, toMin, label: `${fromLabel} - ${toLabel}`, closesAtLabel: toLabel };
  });
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  let isOpen = false;
  let nextCloseLabel = null;
  for (const r of ranges) {
    if (r.toMin < r.fromMin) {
      if (nowMin >= r.fromMin || nowMin <= r.toMin) { isOpen = true; nextCloseLabel = r.closesAtLabel; }
    } else {
      if (nowMin >= r.fromMin && nowMin <= r.toMin) { isOpen = true; nextCloseLabel = r.closesAtLabel; }
    }
  }

  if (!isOpen) {
    const laterRange = ranges.find(r => nowMin < r.fromMin);
    if (laterRange) {
      return {
        status: 'closed',
        text: 'Closed now',
        closesAt: null,
        nextOpen: laterRange.label.split(' - ')[0],
        nextOpenDay: 'Today',
      };
    }
    const next = findNextOpen(1);
    return {
      status: 'closed',
      text: 'Closed now',
      closesAt: null,
      nextOpen: next?.timeLabel || null,
      nextOpenDay: next?.dayLabel || null,
    };
  }

  return {
    status: 'open',
    text: 'Open now',
    closesAt: nextCloseLabel,
    nextOpen: null,
    nextOpenDay: null,
  };
};

const BusinessCard = ({ store, mode, onClick, userFodyes }) => {
  const distanceText = store.distance && store.distance < 99999
    ? store.distance < 1
      ? `${(store.distance * 5280).toFixed(0)} ft`
      : `${store.distance.toFixed(1)} mi`
    : null;

  const fodyesEnabled = store.enableFodyesRewards === true;
  const coinValue = parseFloat(store.fodyesCoinValue) || 0;
  const userBalance = userFodyes?.balance || 0;
  const userBacking = userFodyes?.backingValue || 0;
  // Translation: how many coins at this restaurant the user's backing value equals
  const translatedCoins = (coinValue > 0 && userBacking > 0)
    ? Math.floor(userBacking / coinValue)
    : 0;

  return (
    <div className="ff-business-card" onClick={onClick}>
      <div className="ff-business-image-wrapper">
        {store.design?.logoUrl || store.logoUrl ? (
          <img
            src={store.design?.logoUrl || store.logoUrl}
            alt={store.locationName}
            className="ff-business-image"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="ff-business-image-placeholder">
            <i className="fas fa-store"></i>
          </div>
        )}
        {distanceText && (
          <span className="ff-business-distance">{distanceText}</span>
        )}
      </div>
      <div className="ff-business-body">
        <h3 className="ff-business-name">{store.locationName}</h3>
        <div className="ff-business-rating">
          {store.averageRating > 0 && (
            <>
              <span className="ff-stars">
                {[1,2,3,4,5].map((s) => (
                  <i key={s} className={`fas fa-star${s <= Math.round(store.averageRating) ? '' : '-half-alt'}`} style={{ color: s <= Math.round(store.averageRating) ? '#ffc107' : '#e0e0e0', fontSize: '0.75rem' }}></i>
                ))}
              </span>
              <span className="ff-rating-text">{store.averageRating.toFixed(1)} ({store.totalReviews || 0})</span>
            </>
          )}
          {distanceText && <span className="ff-business-distance-inline">{distanceText}</span>}
        </div>
        <p className="ff-business-address">
          <i className="fas fa-map-marker-alt mr-1"></i>
          {store.address || store.city || 'Address not available'}
        </p>
        {store.phone && (
          <p className="ff-business-phone">
            <i className="fas fa-phone mr-1"></i>
            {store.phone}
          </p>
        )}
        {/* Open/Closed badge for delivery & reserve */}
        {(mode === 'delivery' || mode === 'reserve') && (() => {
          const info = getTodayHoursInfo(store.operationHours);
          if (!info) return null;
          const isOpen = info.status === 'open';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: isOpen ? '#4caf50' : '#f44336',
                boxShadow: `0 0 0 2px ${isOpen ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)'}`
              }}></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isOpen ? '#2e7d32' : '#c62828' }}>
                  {info.text}
                </span>
                {isOpen && info.closesAt && (
                  <span style={{ fontSize: '0.7rem', color: '#555' }}>
                    · Closes at {info.closesAt}
                  </span>
                )}
                {!isOpen && info.nextOpen && info.nextOpenDay && (
                  <span style={{ fontSize: '0.7rem', color: '#555' }}>
                    · Opens at {info.nextOpen} {info.nextOpenDay}
                  </span>
                )}
              </div>
            </div>
          );
        })()}
        <div className="ff-business-footer">
          <span className={`ff-business-badge ${mode}`}>
            {mode === 'delivery' && <><i className="fas fa-motorcycle mr-1"></i> Delivery</>}
            {mode === 'pickup' && <><i className="fas fa-store mr-1"></i> Pick Up</>}
            {mode === 'reserve' && <><i className="fas fa-calendar-check mr-1"></i> Reserve</>}
          </span>
          {fodyesEnabled && (
            <span className="ff-fodyes-badge-inline">
              <i className="fas fa-coins mr-1"></i>
              1 = ${coinValue.toFixed(2)}
              {userBalance > 0 && translatedCoins > 0 && (
                <span className="ff-fodyes-inline-value">· {translatedCoins} yours</span>
              )}
            </span>
          )}
          {store.websiteUrl && (
            <span className="ff-business-link" onClick={(e) => e.stopPropagation()}>
              <a href={store.websiteUrl} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-external-link-alt"></i>
              </a>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCard;
